import React from "react";

import { memoize } from "@use/decorators";

import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";
import { parseSimpleHtml } from "@courses-pm2/api/src/ts/parser/parseSimpleHtml";

/**
 * Specifies the desired length of the preview text, given in number of
 * characters.
 */
const DESIRED_PREVIEW_TEXT_LENGTH = 150;

/**
 * Specification of the available heading tags to use when rendering the title
 * of the accordion.
 */
export type AccordionPanelHeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

/**
 * Specification of data associated with each panel within the accordion.
 */
export type AccordionPanel = {
    /**
     * Contains the list of elements that should be rendered within this panel,
     * when it's expanded.
     */
    content: ElementModel[];

    /**
     * Specifies the title that should be displayed in the header of the panel.
     */
    title: string;

    /**
     * Specifies which heading tag (h1-h6) to use when rendering the title.
     */
    headingTag: AccordionPanelHeadingTag;

    /**
     * If set to true, this panel should be open when the accordion is
     * initially rendered.
     */
    expandInitially: boolean;
};

/**
 * Specification of data needed to instantiate a new AccordionModel.
 */
type AccordionData = {
    [K in "autoCollapse" | "panels"]: AccordionModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class AccordionModel extends ElementModel {
    /**
     * Contains the list of panels to display within this accordion.
     */
    public readonly panels: AccordionPanel[];

    /**
     * If enabled, then only a single panel should be opened at any given time
     * (ie. we auto collapse other panels when a new one is being opened).
     */
    public readonly autoCollapse: boolean;

    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: AccordionData & ElementModelData) {
        super(initial);

        this.autoCollapse = initial.autoCollapse;
        this.panels = initial.panels;
    }

    /**
     * Helper that'll transform the title of the desired panel into React
     * nodes that can be rendered directly.
     */
    @memoize()
    public renderTitle(index: number): React.ReactNode {
        return (
            this.panels[index]
                ? parseSimpleHtml(this.panels[index].title)
                : undefined
        );
    }

    /**
     * Helper that'll transform the content of the desired panel into React
     * nodes that can be rendered directly.
     */
    @memoize()
    public renderPanel(index: number): React.ReactNode[] | undefined {
        const engine = this.engine;

        if (!engine) {
            throw new Error("AccordionModel.renderPanel(): No rendering engine provided to this model.");
        }

        return (
            this.panels[index]
                ? (
                    this.panels[index].content.map((content) => {
                        return React.createElement(
                            React.Fragment,
                            {
                                key: index,
                            },
                            engine.render(content),
                        );
                    })
                )
                : undefined
        );
    }

    /**
     * Helper that'll return an absolute url to a preview image that's
     * representative of the actual element content.
     */
    @memoize()
    public async getPreviewImageUrl(): Promise<string | undefined> {
        for (const panel of this.panels) {
            for (const element of panel.content) {
                const previewImageUrl = await element.getPreviewImageUrl();

                if (previewImageUrl !== undefined) {
                    return previewImageUrl;
                }
            }
        }

        // If we get here, no image preview url is available for any of the
        // content within this accordion
        return undefined;
    }

    /**
     * Helper that'll return a text extract from the component, that can be used
     * as a preview of sorts.
     */
    @memoize()
    public getPreviewText(): string | undefined {
        const previewTexts: string[] = [];

        for (const panel of this.panels) {
            for (const element of panel.content) {
                const previewText = (element.getPreviewText() || "").trim();

                if (previewText !== undefined) {
                    previewTexts.push(previewText);
                }
            }
        }

        // If any preview texts were found, then combind and push them
        if (previewTexts.length > 0) {
            const previewText = previewTexts.join(" ");
            const cropAt = previewText.indexOf(" ", DESIRED_PREVIEW_TEXT_LENGTH);

            return (
                cropAt !== -1
                    ? `${previewText.substr(0, cropAt)}...`
                    : previewText
            );
        }

        // If we get here, no text extract is available for any of the content
        // within this accordion
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Iterate over all children of the accordion, and accumulate their combined
     * count of standard pages.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return this.panels.reduce(
            (previousValue, accordionPanel) => previousValue + reduceAccordionPanelStandardPageCount(accordionPanel.content),
            0,
        );
    }
}

/**
 * Reduce the list of content elements in an accordion panel to their
 * accumulated count of standard pages.
 */
function reduceAccordionPanelStandardPageCount(elements: ElementModel[]): number {
    return elements.reduce(
        (previousValue, element) => previousValue + element.getStandardPageCount(),
        0,
    );
}
